import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { ApiError, downloadFile } from '../api/request';
import { downloadBase64 } from '../utils/exportCsv';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** 后端批量导入接口的通用返回形状 (材料 / SKU 共用). */
export interface XlsxImportResult {
    total: number;
    valid: number;
    invalid: { row: number; reason: string }[] | null;
    /** 普通用户: 走审批流的工单号; 直接入库时为空 */
    taskCode?: string;
    /** 直接入库时的明细计数 (导入语义是 upsert 的接口才有) */
    created?: number;
    updated?: number;
    /** 关联绑定计数 (SKU 导入: 材质编码匹配上 / 留空未绑定的行数) */
    bound?: number;
    unbound?: number;
    /** 未绑定明细 (行照常入库, 只是没绑材料, 需人工确认) */
    unboundRows?: { row: number; reason: string }[] | null;
    /** 导入结果回执 xlsx (base64): 原表 + 末列每行结果; 非全部成功时后端才生成 */
    resultFile?: string;
    resultFileName?: string;
}

interface Props<T extends XlsxImportResult> {
    open: boolean;
    onClose: () => void;
    /** 直接入库成功后回调 (走审批流时数据未变, 不触发) */
    onImported?: () => void;
    title: string;
    /** 模板下载路径 (相对 API_BASE) */
    templatePath: string;
    /** 模板保存文件名; 缺省用后端 Content-Disposition, 再兜底 import-template.xlsx */
    templateName?: string;
    /** 上传实现 (各资源自己的 import 接口) */
    upload: (file: File) => Promise<T>;
    /** 模板说明区的补充文案 */
    hint?: ReactNode;
    /**
     * 结果统计条上各资源自己的计数 (替代默认的「新建 X · 更新 Y」).
     * 导入语义不是 upsert 的资源用它给自己的口径 (如小贴士: 新建条目 / 复用 / 新增挂载).
     */
    renderStats?: (result: T) => ReactNode;
}

// 通用 xlsx 批量导入 modal: 下载模板 → 选择/拖拽文件 → 上传 → 展示校验结果.
// 有效行直接入库 (仅本地开发) 或整批提一个审批工单, 无效行逐行给出原因.
function XlsxImportModal<T extends XlsxImportResult>({
    open,
    onClose,
    onImported,
    title,
    templatePath,
    templateName,
    upload,
    hint,
    renderStats,
}: Props<T>) {
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<T | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [tplBusy, setTplBusy] = useState(false);
    const [tplErr, setTplErr] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) {
            setFile(null);
            setBusy(false);
            setError('');
            setResult(null);
            setDragOver(false);
        }
    }, [open]);

    const pickFile = (f: File | null | undefined) => {
        if (!f) return;
        if (!/\.xlsx$/i.test(f.name)) {
            setError('仅支持 .xlsx 文件');
            return;
        }
        setFile(f);
        setResult(null);
        setError('');
    };

    const onSubmit = async () => {
        if (!file) {
            setError('请先选择 xlsx 文件');
            return;
        }
        setBusy(true);
        setError('');
        setResult(null);
        try {
            const res = await upload(file);
            setResult(res);
            // 非全部成功时后端回一份带"导入结果"列的 xlsx, 直接落盘一份给运营核对
            if (res.resultFile) {
                downloadBase64(
                    res.resultFileName || 'import-result.xlsx',
                    res.resultFile,
                    XLSX_MIME,
                );
            }
            // 有 taskCode = 走审批流, 数据未入库, 不刷新列表
            if (!res.taskCode && res.valid > 0 && onImported) onImported();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy(false);
        }
    };

    // 模板下载必须走 downloadFile (fetch 带 utoken), 不能用 <a href download>:
    // 浏览器直接跳转带不上鉴权头, 线上必然 20101 "token验证失效".
    const onDownloadTemplate = async () => {
        setTplBusy(true);
        setTplErr('');
        try {
            await downloadFile(templatePath, templateName || 'import-template.xlsx');
        } catch (e) {
            setTplErr(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setTplBusy(false);
        }
    };

    const invalid = result?.invalid || [];
    const unboundRows = result?.unboundRows || [];

    return (
        <Modal open={open} onClose={onClose} title={title} width={720}>
            <div className="space-y-4">
                <div className="text-xs text-slate-500">
                    先
                    <a
                        className="text-brand-600 hover:underline mx-1 cursor-pointer"
                        onClick={onDownloadTemplate}
                    >
                        {tplBusy ? '下载中…' : '下载导入模板'}
                    </a>
                    按模板填写后上传; 有效行入库 (普通用户走审批, 管理员通过后生效), 无效行会逐行给出原因.
                    {tplErr && <div className="mt-1 text-rose-600">模板下载失败: {tplErr}</div>}
                    {hint && <div className="mt-1">{hint}</div>}
                </div>

                <div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        pickFile(e.dataTransfer.files?.[0]);
                    }}
                    className={`border-2 border-dashed rounded-lg p-6 text-center text-sm cursor-pointer ${
                        dragOver
                            ? 'border-brand-400 bg-brand-50/40'
                            : 'border-slate-200 hover:border-slate-300'
                    }`}
                >
                    {file ? (
                        <div className="text-slate-700 font-mono text-xs">
                            {file.name}{' '}
                            <span className="text-slate-400">
                                ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                        </div>
                    ) : (
                        <span className="text-slate-400">点击选择 或 拖拽 xlsx 文件到此处</span>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        onChange={(e) => {
                            pickFile(e.target.files?.[0]);
                            e.target.value = '';
                        }}
                    />
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                {result && (
                    <div className="space-y-3">
                        <div
                            className={`card p-3 text-sm ${
                                invalid.length + unboundRows.length === 0
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                    : 'border-amber-200 bg-amber-50 text-amber-800'
                            }`}
                        >
                            <div className="font-semibold">
                                {invalid.length + unboundRows.length === 0
                                    ? `全部成功: ${result.valid} / ${result.total} 行`
                                    : `成功 ${result.valid - unboundRows.length} 行 · 需处理 ${
                                          invalid.length + unboundRows.length
                                      } 行 (共 ${result.total} 行)`}
                            </div>
                            {result.resultFile && (
                                <div className="mt-1 flex items-center gap-2 text-xs">
                                    <span>
                                        已生成导入结果 xlsx (原表末列附每行结果/失败原因), 已自动下载
                                    </span>
                                    <button
                                        type="button"
                                        className="underline hover:no-underline"
                                        onClick={() =>
                                            downloadBase64(
                                                result.resultFileName || 'import-result.xlsx',
                                                result.resultFile as string,
                                                XLSX_MIME,
                                            )
                                        }
                                    >
                                        重新下载
                                    </button>
                                </div>
                            )}
                        </div>
                        {result.taskCode && (
                            <div className="card p-3 text-sm text-amber-700 border-amber-200 bg-amber-50">
                                已提交审批工单{' '}
                                <span className="font-mono font-semibold">{result.taskCode}</span>
                                , 有效行待管理员通过后入库, 可在「我的工单」跟踪进度.
                            </div>
                        )}
                        <div className="card overflow-hidden">
                            <div className="px-3 py-2 bg-slate-50 text-sm flex items-center gap-3">
                                <span>共 {result.total} 行</span>
                                <span className="text-emerald-600">有效 {result.valid}</span>
                                {renderStats
                                    ? renderStats(result)
                                    : result.created !== undefined && (
                                          <span className="text-slate-500">
                                              新建 {result.created} · 更新 {result.updated ?? 0}
                                          </span>
                                      )}
                                {result.bound !== undefined && (
                                    <span className="text-slate-500">
                                        绑定材质 {result.bound} · 未绑定 {result.unbound ?? 0}
                                        {unboundRows.length > 0 && ` (其中 ${unboundRows.length} 行需人工确认)`}
                                    </span>
                                )}
                                {invalid.length > 0 && (
                                    <span className="text-rose-600">无效 {invalid.length}</span>
                                )}
                            </div>
                            {invalid.length > 0 && (
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-t border-slate-100">
                                        <tr>
                                            <th className="th w-16">行号</th>
                                            <th className="th">无效原因</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invalid.map((r) => (
                                            <tr key={r.row}>
                                                <td className="td font-mono text-xs">{r.row}</td>
                                                <td className="td text-xs text-rose-600">
                                                    {r.reason}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {unboundRows.length > 0 && (
                            <div className="card overflow-x-auto">
                                <div className="px-3 py-2 bg-amber-50 text-sm text-amber-800">
                                    待人工确认: {unboundRows.length} 行已入库但<b>未绑定材质</b>
                                    <span className="text-amber-700/80">
                                        {' '}
                                        (材质编码不在材料库; 多个材质用逗号分隔的会各自绑定, 只有未命中的部分列在这里) ——
                                        请在 SKU 库里逐条补绑
                                    </span>
                                </div>
                                <div className="max-h-64 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 border-t border-slate-100">
                                            <tr>
                                                <th className="th w-16">行号</th>
                                                <th className="th">原因</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {unboundRows.map((r) => (
                                                <tr key={r.row}>
                                                    <td className="td font-mono text-xs">
                                                        {r.row}
                                                    </td>
                                                    <td className="td text-xs text-amber-700">
                                                        {r.reason}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" onClick={onClose} className="btn-ghost" disabled={busy}>
                        {result ? '关闭' : '取消'}
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onSubmit}
                        disabled={!file || busy}
                    >
                        {busy ? '上传中…' : '上传导入'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default XlsxImportModal;
